import { Link } from "react-router";
import { listCollection, readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import { ANALYSES } from "../lib/analyses.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import LoopVideo from "../components/LoopVideo/LoopVideo.jsx";
import CodeChip from "../components/CodeChip/CodeChip.jsx";
import VideoReel from "../components/VideoReel/VideoReel.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CaseStudyShowcase from "../components/CaseStudyShowcase/CaseStudyShowcase.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./rsa3d.module.css";

const PAGE = {
  title: "Road Safety Audit 3D",
  description:
    "Proactive road safety analysis in 3D: stopping and intersection sight distance, pedestrians, cyclists, clear zones and more, to AASHTO and state DOT standards.",
  path: "/road-safety-audit-3d",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Road Safety Audit 3D", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/rsa3d/sight-distance",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

export async function loader() {
  const dots = readJson("clients").filter((c) => c.type === "dot").length;
  const caseStudies = listCollection("case-studies").filter(
    (e) => e.product === "rsa3d" || e.product === "both"
  );
  return { dots, caseStudies };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["inputs", "videos"];
const surface = surfaceFor(LIGHT_SECTIONS);

const INPUTS = [
  {
    title: "Existing roads",
    text: "A scan of the road as it is today.",
    formats: ["LiDAR point clouds", "Reality mesh", "Digital terrain model"],
  },
  {
    title: "Proposed designs",
    text: "The design files your team already works in.",
    formats: ["DGN", "DWG", "OpenRoads", "InfraWorks", "Civil 3D", "SketchUp"],
  },
];

const REPORTING = [
  {
    title: "Your standards",
    text: "AASHTO and state DOT standards out of the box, and every parameter adjustable.",
  },
  {
    title: "Every finding explained",
    text: "Each deficiency comes with the 3D view that shows why it fails.",
  },
  {
    title: "Reports people can use",
    text: "Downloadable CSV, online HTML reports and Google Maps integration.",
  },
  {
    title: "Your systems",
    text: "Results integrate with your own data systems.",
  },
];

export default function Rsa3d({ loaderData }) {
  const { dots, caseStudies } = loaderData;
  const videos = [
    "rsaSightDistance",
    "rsaPlanningDesign",
    "rsaExistingRoads",
    "rsaFullReport",
    "rsaAdditionalSafety",
  ].map((k) => SITE.videos[k]);
  return (
    <>
      <section className={styles.hero} aria-labelledby="rsa-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="rsa-title" className={styles.heroTitle}>
              Find the hazards before the crash, not after
            </h1>
            <p className={styles.heroLead}>
              A systemic audit of the whole 3D road environment that does not wait for crash
              history. Faster than a traditional audit, and it looks everywhere.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <Link to="/road-safety-audit-3d/why-road-safety-audits" className={styles.heroLink}>
                Why road safety audits
              </Link>
            </div>
            <p className={styles.heroNote}>
              From RDV Systems, trusted by {dots} state DOTs since {SITE.foundedYear}.
            </p>
          </div>
          <LoopVideo
            src="/videos/rsa-loop.mp4"
            poster="/images/site/home/rsa-loop.jpg"
            caption="Sight distance checked on a LiDAR scan of an existing road"
            focus="left center"
            ratio="16 / 11"
          />
        </div>
      </section>

      {/* The page's signature: every analysis on one board, each with its own code tile. */}
      <section className="section" aria-labelledby="analyses-title">
        <div className="container">
          <SectionHeader
            id="analyses-title"
            title="Ten analyses, one 3D road"
            aside="Every lane, every direction and every intersection, checked the same way. Not a sample of stations a reviewer chose."
            link={{ to: "/123bimlive", label: "See each on a simulated corridor" }}
          />
          <ul className={styles.analyses}>
            {ANALYSES.map((a) => (
              <li key={a.key}>
                <CodeChip code={a.code} tint={a.tint} />
                <h3>{a.title}</h3>
                <p>{a.does}</p>
              </li>
            ))}
          </ul>
          <p className={styles.analysesNote}>
            The same 3D road also answers pedestrian and cyclist sight lines and bridge vertical
            clearances.
          </p>
        </div>
      </section>

      <section className="section" data-surface={surface("inputs")} aria-labelledby="inputs-title">
        <div className="container">
          <SectionHeader
            id="inputs-title"
            title="Start from what you already have"
            aside="No conversion project on your side. Send the files, and we prepare the 3D road data."
          />
          <div className={styles.inputs}>
            {INPUTS.map((input) => (
              <div key={input.title} className={styles.input}>
                <h3>{input.title}</h3>
                <p>{input.text}</p>
                <ul className={styles.formats} aria-label={`${input.title}: accepted inputs`}>
                  {input.formats.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="reporting-title">
        <div className={`container ${styles.reporting}`}>
          <div>
            <SectionHeader id="reporting-title" title="Your standards, your reports" />
            <ul className={styles.reportList}>
              {REPORTING.map((r) => (
                <li key={r.title}>
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                </li>
              ))}
            </ul>
          </div>
          <Picture
            src="/images/123bimlive/online-report"
            alt="The online stopping sight distance report: the LiDAR scan rendered from the driver's eye with map and zoom insets, beside a table of every station's visibility, obstruction, milepost, slope and maximum safe speed"
            ratio="2549 / 1344"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 55vw, 100vw"
            className={styles.reportImage}
          />
        </div>
      </section>

      <section className="section" data-surface={surface("videos")} aria-labelledby="videos-title">
        <div className="container">
          <SectionHeader
            id="videos-title"
            title="Five short demos"
            aside="Sight distance, a design, an existing road, the full report and the other safety checks."
          />
          <VideoReel videos={videos} label="Road Safety Audit 3D videos" />
        </div>
      </section>

      {caseStudies.length ? (
        <section className="section" aria-labelledby="work-title">
          <div className="container">
            <SectionHeader
              id="work-title"
              title="Road Safety Audit 3D in the field"
              link={{ to: "/case-studies?product=rsa3d", label: "All case studies" }}
            />
            <CaseStudyShowcase items={caseStudies} label="Road Safety Audit 3D case studies" />
          </div>
        </section>
      ) : null}

      <CTABand
        variant="statement"
        image="/images/site/rsa3d/sight-distance"
        title="Audit your next project in 3D"
        text="Send us a design or a scan and get a sight-distance report back, with no software to install."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
